import { Component, Element, Listen, Prop } from '@stencil/core';

@Component({
  tag: 'mama-accordion',
  styleUrl: './mama-accordion.scss',
})
export class MamaAccordion {
  @Element() el!: HTMLElement;

  @Prop() firstOpen: false;
  @Prop() keepAllOpen: false;

  // Store the animation object (so we can cancel it if needed)
  animation = null;
  // Store if the element is closing
  isClosing = false;
  // Store if the element is expanding
  isExpanding = false;

  componentDidLoad() {
    // If the firstOpen prop is set to true, open the first details element
    if (this.firstOpen) {
      const firstDetails = this.el.querySelector('details');
      if (firstDetails) {
        this.open(firstDetails as HTMLDetailsElement);
      }
    }
  }

  @Listen('click')
  onClick(event: MouseEvent) {
    if ((event.target as HTMLElement).nodeName.toLowerCase() !== 'summary') {
      return;
    }

    event.preventDefault();
    const target = event.target as HTMLElement;
    const detailsElement = target.parentNode as HTMLDetailsElement;

    let openElements = [];
    if (!this.keepAllOpen) {
      openElements = Array.from(this.el.querySelectorAll('details[open]')).filter(el => el !== detailsElement) as HTMLDetailsElement[];
    }

    // Add an overflow on the <details> to avoid content overflowing
    detailsElement.style.overflow = 'hidden';

    // Check if the element is being closed or is already closed
    if (this.isClosing || !detailsElement.open) {
      this.open(detailsElement);
      // Check if the element is being openned or is already open
    } else if (this.isExpanding || detailsElement.open) {
      this.shrink(detailsElement);
    }

    openElements.forEach((el: HTMLDetailsElement) => (el.open = false));
  }

  private open(detailsElement: HTMLDetailsElement) {
    // Apply a fixed height on the element
    detailsElement.style.height = `${detailsElement.offsetHeight}px`;
    // Force the [open] attribute on the details element
    detailsElement.open = true;
    // Wait for the next frame to call the expand function
    window.requestAnimationFrame(() => this.expand(detailsElement));
  }

  private expand(detailsElement: HTMLDetailsElement) {
    // Set the element as "being expanding"
    this.isExpanding = true;
    // Get the current fixed height of the element
    const startHeight = `${detailsElement.offsetHeight}px`;

    const summary: HTMLElement = detailsElement.querySelector('summary');
    const content: HTMLElement = detailsElement.querySelector('.collapse-content');
    // Calculate the open height of the element (summary height + content height)
    const endHeight = `${summary.offsetHeight + content.offsetHeight}px`;

    // If there is already an animation running
    if (this.animation) {
      // Cancel the current animation
      this.animation.cancel();
    }

    // Start a WAAPI animation
    this.animation = detailsElement.animate(
      {
        // Set the keyframes from the startHeight to endHeight
        height: [startHeight, endHeight],
      },
      {
        duration: 200,
        easing: 'ease-out',
      },
    );
    // When the animation is complete, call onAnimationFinish()
    this.animation.onfinish = () => this.onAnimationFinish(detailsElement, true);
    // If the animation is cancelled, isExpanding variable is set to false
    this.animation.oncancel = () => (this.isExpanding = false);
    detailsElement.classList.add('is-opening');
  }

  private shrink(detailsElement: HTMLDetailsElement) {
    // Set the element as "being closed"
    this.isClosing = true;

    const summary: HTMLElement = detailsElement.querySelector('summary');

    // Store the current height of the element
    const startHeight = `${detailsElement.offsetHeight}px`;
    // Calculate the height of the summary
    const endHeight = `${summary.offsetHeight}px`;

    // If there is already an animation running
    if (this.animation) {
      // Cancel the current animation
      this.animation.cancel();
    }

    // Start a WAAPI animation
    this.animation = detailsElement.animate(
      {
        // Set the keyframes from the startHeight to endHeight
        height: [startHeight, endHeight],
      },
      {
        duration: 200,
        easing: 'ease-out',
      },
    );

    // When the animation is complete, call onAnimationFinish()
    this.animation.onfinish = () => this.onAnimationFinish(detailsElement, false);
    // If the animation is cancelled, isClosing variable is set to false
    this.animation.oncancel = () => (this.isClosing = false);
    detailsElement.classList.add('is-closing');
  }

  private onAnimationFinish(detailsElement: HTMLDetailsElement, open: boolean) {
    // Set the open attribute based on the parameter
    detailsElement.open = open;
    // Clear the stored animation
    this.animation = null;
    // Reset isClosing & isExpanding
    this.isClosing = false;
    this.isExpanding = false;
    // Remove the overflow hidden and the fixed height
    detailsElement.style.height = detailsElement.style.overflow = '';
    detailsElement.classList.remove('is-opening');
    detailsElement.classList.remove('is-closing');
  }
}
